import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/api/client";
import PropertyCard from "@/components/PropertyCard";
import { Link } from "react-router-dom";
import { Heart, SlidersHorizontal } from "lucide-react";
import { useFavorites } from "@/context/FavoritesContext";

export default function ImoveisPage() {
  const [properties, setProperties] = useState([]);
  const [zones, setZones] = useState([]);
  const [zoneFilter, setZoneFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [typologyFilter, setTypologyFilter] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [statusFilter, setStatusFilter] = useState("active"); // active | all | vendido
  const [sort, setSort] = useState("recent");
  const { count } = useFavorites();

  useEffect(() => {
    api.get("/properties").then((r) => setProperties(r.data)).catch(() => {});
    api.get("/zones").then((r) => setZones(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    const STATUS_ORDER = { "à venda": 0, "reservado": 1, "vendido": 2 };
    let list = properties.filter((p) => {
      const s = (p.status || "").toLowerCase();
      if (statusFilter === "active" && s === "vendido") return false;
      if (statusFilter === "vendido" && s !== "vendido") return false;
      if (zoneFilter && p.zone_slug !== zoneFilter) return false;
      if (typeFilter && p.property_type !== typeFilter) return false;
      if (typologyFilter && p.typology !== typologyFilter) return false;
      if (minPrice && Number(p.price) < Number(minPrice)) return false;
      if (maxPrice && Number(p.price) > Number(maxPrice)) return false;
      return true;
    });
    if (sort === "price-asc") list = [...list].sort((a, b) => a.price - b.price);
    else if (sort === "price-desc") list = [...list].sort((a, b) => b.price - a.price);
    else if (sort === "area-desc") list = [...list].sort((a, b) => (b.area || 0) - (a.area || 0));
    // Always keep status priority (à venda → reservado → vendido) as primary order.
    list = [...list].sort((a, b) => {
      const sa = STATUS_ORDER[(a.status || "").toLowerCase()] ?? 99;
      const sb = STATUS_ORDER[(b.status || "").toLowerCase()] ?? 99;
      return sa - sb;
    });
    return list;
  }, [properties, zoneFilter, typeFilter, typologyFilter, minPrice, maxPrice, statusFilter, sort]);

  const types = Array.from(new Set(properties.map((p) => p.property_type))).filter(Boolean);
  const typologies = Array.from(new Set(properties.map((p) => p.typology))).filter(Boolean);

  const clearAll = () => {
    setZoneFilter(""); setTypeFilter(""); setTypologyFilter("");
    setMinPrice(""); setMaxPrice(""); setStatusFilter("active"); setSort("recent");
  };
  const active = [zoneFilter, typeFilter, typologyFilter, minPrice, maxPrice].filter(Boolean).length + (statusFilter !== "active" ? 1 : 0);

  const inputCls = "rounded-full border border-stone-200 bg-white px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#103C4A]/30";

  return (
    <div data-testid="page-imoveis">
      <section className="container-cnp pt-14 lg:pt-20 pb-8">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <span className="tag-chip">Imóveis</span>
            <h1 className="mt-4 font-display text-4xl md:text-5xl text-stone-900">Imóveis no Porto e Grande Porto</h1>
            <p className="mt-4 max-w-2xl text-stone-600">Uma seleção cuidada, atualizada regularmente. Filtre por zona, tipo, tipologia ou preço.</p>
          </div>
          <Link to="/favoritos" data-testid="link-favoritos" className="btn-secondary">
            <Heart className={`h-4 w-4 ${count > 0 ? "fill-[#103C4A]" : ""}`} /> Favoritos {count > 0 && <span className="ml-1 tag-chip bg-[#103C4A] text-white">{count}</span>}
          </Link>
        </div>
      </section>

      <section className="container-cnp pb-6">
        <div className="card-soft p-4 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-stone-500 pr-2 border-r border-stone-200">
            <SlidersHorizontal className="h-4 w-4" /> Filtros
          </div>
          <select value={zoneFilter} onChange={(e) => setZoneFilter(e.target.value)} data-testid="imoveis-filter-zone" className={inputCls}>
            <option value="">Todas as zonas</option>
            {zones.map((z) => (<option key={z.id} value={z.slug}>{z.name}</option>))}
          </select>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} data-testid="imoveis-filter-type" className={inputCls}>
            <option value="">Todos os tipos</option>
            {types.map((t) => (<option key={t} value={t}>{t}</option>))}
          </select>
          <select value={typologyFilter} onChange={(e) => setTypologyFilter(e.target.value)} data-testid="imoveis-filter-typology" className={inputCls}>
            <option value="">Qualquer tipologia</option>
            {typologies.map((t) => (<option key={t} value={t}>{t}</option>))}
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="imoveis-filter-status" className={inputCls}>
            <option value="active">À venda e reservados</option>
            <option value="all">Incluir vendidos</option>
            <option value="vendido">Só vendidos</option>
          </select>
          <div className="flex items-center gap-1">
            <input type="number" placeholder="Min €" value={minPrice} onChange={(e) => setMinPrice(e.target.value)} data-testid="imoveis-filter-min-price" className={`${inputCls} w-28`} />
            <span className="text-stone-400 text-sm">–</span>
            <input type="number" placeholder="Max €" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} data-testid="imoveis-filter-max-price" className={`${inputCls} w-28`} />
          </div>
          <div className="ml-auto flex items-center gap-2">
            <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="imoveis-sort" className={inputCls}>
              <option value="recent">Mais recentes</option>
              <option value="price-asc">Preço: menor primeiro</option>
              <option value="price-desc">Preço: maior primeiro</option>
              <option value="area-desc">Maior área</option>
            </select>
            {active > 0 && (
              <button onClick={clearAll} data-testid="imoveis-filter-clear" className="btn-ghost">Limpar</button>
            )}
          </div>
        </div>
        <div className="mt-3 text-sm text-stone-500">
          <span data-testid="imoveis-results-count">{filtered.length}</span> {filtered.length === 1 ? "imóvel" : "imóveis"}
        </div>
      </section>

      <section className="container-cnp pb-16">
        {filtered.length === 0 ? (
          <div className="card-soft p-10 text-center text-stone-600">Sem imóveis a mostrar para estes filtros.</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((p) => <PropertyCard key={p.id} property={p} />)}
          </div>
        )}
      </section>
    </div>
  );
}
